import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, ArrowUpRight } from "@phosphor-icons/react";
import { useLang } from "../i18n/LangContext";
import { KenBurns } from "./KenBurns";
import { Magnetic } from "./Magnetic";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: 0,
    transition: {
      delay: 0.2 + i * 0.14,
      duration: 1.15,
      ease: [0.65, 0, 0.15, 1],
    },
  }),
};

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.9, ease: [0.7, 0, 0.2, 1] },
  },
});

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.92]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#0A0A0B] text-[#F1EEE8]"
      data-testid="hero-section"
    >
      {/* Parallax cinematic background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0"
      >
        <KenBurns
          duration={9500}
          images={[
            "/hero/hero-1.webp",
            "/hero/hero-2.webp",
            "/hero/hero-3.webp",
            "/hero/hero-4.webp",
          ]}
        />
      </motion.div>

      {/* Deep Lake gradient overlay */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-10"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B]/55 via-[#0A0A0B]/45 to-[#0A0A0B]" />
      </motion.div>

      {/* Cinematic warm color grading — golden/blue hour tint */}
      <div
        className="absolute inset-0 z-10 pointer-events-none mix-blend-soft-light"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,180,120,0.18) 0%, rgba(20,40,80,0.12) 55%, rgba(10,10,15,0.28) 100%)",
        }}
      />

      {/* Vignette */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 32%, rgba(10,10,11,0.92) 100%)",
        }}
      />

      {/* Letterbox removed — video fills viewport */}

      {/* Content */}
      <motion.div
        style={{ y: textY }}
        className="relative z-30 max-w-[1600px] mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-16 min-h-[100svh] flex flex-col justify-between"
      >
        <div>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp(0.15)}
            className="flex items-center gap-4 text-[#F1EEE8]/75 text-[0.7rem] tracking-[0.28em] uppercase"
          >
            <span className="w-2 h-2 rounded-full bg-[#13535A] rouge-pulse" />
            <span>{t.hero.overline}</span>
          </motion.div>

          <h1 className="mt-8 md:mt-12 font-display font-medium leading-[1.02] tracking-tight text-[clamp(2.6rem,7.5vw,8.8rem)]">
            {[t.hero.line1, t.hero.line2, t.hero.line3].map((line, i) => (
              <span key={i} className="block">
                <span className="reveal-mask">
                  <motion.span
                    className="inline-block"
                    variants={lineVariants}
                    custom={i}
                    initial="hidden"
                    animate="visible"
                  >
                    {line}
                    {i === 2 && (
                      <>
                        {" "}
                        <span
                          className="italic-serif text-[#F1EEE8]/95"
                          style={{ textShadow: "0 2px 24px rgba(0,0,0,0.55)" }}
                        >
                          {t.hero.line3Accent}
                        </span>
                      </>
                    )}
                  </motion.span>
                </span>
              </span>
            ))}
          </h1>
        </div>

        <div className="mt-14 md:mt-20 grid md:grid-cols-12 gap-10 md:gap-8 items-end">
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp(1.4)}
            className="md:col-span-6 md:col-start-7 text-[#F1EEE8]/80 text-base md:text-[1.05rem] leading-relaxed max-w-xl"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp(1.6)}
            className="md:col-span-6 md:col-start-7 flex flex-col sm:flex-row gap-4"
          >
            <Magnetic strength={0.25}>
              <a
                href="#contact"
                className="btn-primary-inverse"
                data-testid="hero-cta-primary"
              >
                {t.hero.ctaPrimary}
                <ArrowUpRight size={16} weight="light" />
              </a>
            </Magnetic>
            <Magnetic strength={0.2}>
              <a
                href="#protocol"
                className="btn-ghost-dark"
                data-testid="hero-cta-secondary"
              >
                {t.hero.ctaSecondary}
              </a>
            </Magnetic>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="absolute left-6 md:left-10 bottom-6 flex items-center gap-3 text-[#F1EEE8]/60 text-[0.7rem] tracking-[0.25em] uppercase"
        >
          <motion.span
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <ArrowDown size={14} weight="light" />
          </motion.span>
          <span>{t.hero.scroll}</span>
        </motion.div>
      </motion.div>
    </section>
  );
};
